/** Signing in: who you are first, then where to. Pure, ported from the prototype's lib/signin.ts. M11 round one: the welcome step (after
 *  the code, for an address in no workspace) and the creation form (`step=new`), and the landing order after the proof and on "/". */
import type { WorkspaceAddress } from "@omnabase/shared";

export type SignInStep = "email" | "code" | "workspace" | "welcome" | "new";
export const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
/** the six digits from the mail; spaces between them are fine */
export const isCode = (v: string) => /^\d{6}$/.test(v.replace(/\s+/g, ""));
export const normaliseCode = (v: string) => v.replace(/\s+/g, "");
const STEPS: readonly SignInStep[] = ["code", "workspace", "welcome", "new"];
export const stepFrom = (v: string | null): SignInStep => (STEPS.includes(v as SignInStep) ? (v as SignInStep) : "email");

/** Where a person lands: a workspace to go into, or a step (an object, since "welcome" is also a legal workspace address) */
export type Landing = { slug: string } | { step: "welcome" | "workspace" };
/** The brief's order (choice 5): the workspace the address names; the invitation the person followed or that was accepted for them; the
 *  workspace they opened last; their only one; none → the welcome step, several → the chooser. A named, invited or last workspace the
 *  person is not (or no longer) a member of is skipped. */
export function landingAfterProof(i: { workspaces: { slug: string }[]; lastWorkspace: string | null; named: string | null; invitation: string | null }): Landing {
  const member = (slug: string | null): slug is string => slug !== null && i.workspaces.some((w) => w.slug === slug);
  for (const slug of [i.named, i.invitation, i.lastWorkspace]) if (member(slug)) return { slug };
  if (i.workspaces.length === 1) return { slug: i.workspaces[0]!.slug };
  return { step: i.workspaces.length === 0 ? "welcome" : "workspace" };
}
/** the address a landing leads to */
export const landingPath = (l: Landing) => ("slug" in l ? `/${l.slug}` : `/signin?step=${l.step}`);

/** the message under the form for what the API answered */
export function problemFor(status: number, code?: string): string {
  if (code === "TURNSTILE") return "The browser check failed. Reload the page and try again.";
  if (code === "CODE_LIMIT") return "Too many codes for this address. Use the newest code in your mail, or try again later.";
  if (status === 429) return "Too many tries. Wait a minute and try again.";
  if (status === 400 || status === 401 || status === 403) return "That code is not right, or it has expired.";
  return "Something went wrong. Try again.";
}

/** "Weber Fahrrad" → "weber-fahrrad": the creation form's proposed address (accents dropped, cut to 40; the check says if it is free) */
export function proposeSlug(name: string): string {
  return name.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40).replace(/-+$/g, "");
}

/** The creation form's sentence under the address (the founder, 2026-10-07): the typed address in both places, live, and the placeholder
 *  word until something is typed */
export const ADDRESS_PLACEHOLDER = "<address>";
export function addressPlaces(slug: string): { app: string; mail: string } {
  const a = slug.trim() || ADDRESS_PLACEHOLDER;
  return { app: `app.omnabase.com/${a}`, mail: `${a}.omnabase.com` };
}
/** The address check's verdict on its own reserved line: short enough for one line of the form at phone width (the sentence below already
 *  shows the address; the API's longer words stay the problem said on Create). Null while there is no verdict. */
const ADDRESS_VERDICT: Record<WorkspaceAddress["status"], string> = {
  free: "This address is free.",
  taken: "This address is taken. Try another.",
  reserved: "This address is reserved for OmnaBase.",
  retired: "A deleted workspace had it. Try another.",
  shape: "Use a to z, 0 to 9 and inner hyphens.",
};
export const addressVerdict = (check: WorkspaceAddress | null): string | null => (check ? ADDRESS_VERDICT[check.status] : null);

/** M11 round two: what the creation form says when the create is refused: while the founder has paused new workspaces (the back office's
 *  sign-up switch, the hook's `SIGNUP_CLOSED`) the coordinator's sentence, which points to the way that still works; else the API's own words */
export const SIGNUP_PAUSED = "New workspaces are paused for a moment. You can still join one by invitation.";
export const creationProblem = (message: string, details: unknown): string => ((details as { code?: string } | undefined)?.code === "SIGNUP_CLOSED" ? SIGNUP_PAUSED : message);

/** The signed-out sheet's way back (the forum sync round, 2026-11-02; brief rule 9, review findings 19 and 21): the sign-in page with the
 *  place to return to as `next`. */
export const signInAgainPath = (appPath: string) => `/signin?next=${encodeURIComponent(appPath)}`;
/** Where to land after the proof when the sign-in carried `next`: only a path of this app (one leading "/", no second "/" or "\" after it, no
 *  control characters), never the sign-in page itself, and only into a workspace the person who just signed in belongs to (a colleague signing
 *  in on the same computer never lands in someone else's workspace); its search and hash are kept. Anything else: null, land as before. */
export function nextPath(next: string | null, workspaces: { slug: string }[]): string | null {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return null;
  if (/[\u0000-\u001f\u007f]/.test(next)) return null;
  const first = next.slice(1).split(/[/?#]/)[0] ?? "";
  if (first === "signin" || !workspaces.some((w) => w.slug === first)) return null;
  return next;
}
